/**
 * Corrects every product title, and variant option name/value, on the page to the true canonical
 * (default-locale) text, on /uk only - see snippets/localized-title.liquid, localized-option-
 * name.liquid, and localized-option-value.liquid's own doc comments for why this exists: Shopify
 * resolves product.title and product.options_with_values per the current request's locale, before
 * any Liquid runs, so a product with a published Ukrainian translation renders that translated
 * value server-side regardless of theme code. Fetching /products/{handle}.js with NO locale
 * prefix always returns the shop's primary/default-locale data (confirmed live - same product id,
 * only translatable fields differ from the /uk-prefixed endpoint), so this corrects the DOM to
 * match that live value - a query against the canonical source itself, not a second copy of data.
 *
 * Runs once on initial load (covers every server-rendered marker: product page, cards,
 * collections, Home, search, recommendations) and again whenever new markers appear later in the
 * page (cart drawer/full cart line items added or re-rendered after this script's first pass) -
 * see the MutationObserver below for why that specific mechanism was needed for the cart case.
 */
(function () {
  'use strict';

  if (document.documentElement.lang !== 'uk') return;

  var TITLE_SELECTOR = '.js-live-product-title[data-product-handle]';
  var OPTION_NAME_SELECTOR = '.js-live-option-name[data-product-handle]';
  var OPTION_VALUE_SELECTOR = '.js-live-option-value[data-product-handle]';
  var ALL_SELECTOR = TITLE_SELECTOR + ', ' + OPTION_NAME_SELECTOR + ', ' + OPTION_VALUE_SELECTOR;

  var cache = new Map();

  // Mirrors snippets/size-display-name.liquid's own static lookup table exactly - duplicated
  // deliberately rather than shared across Liquid/JS, since there is no third mechanism both
  // could call. Only applied to values marked data-size-value="true" by the Liquid side, which
  // already gates this on the option actually being "Size".
  var SIZE_DISPLAY_NAMES = {
    XS: 'Extra Small',
    S: 'Small',
    M: 'Medium',
    L: 'Large',
    XL: 'Extra Large',
    XXL: '2X Large',
    '2XL': '2X Large',
    '3XL': '3X Large'
  };

  function sizeDisplayName(value) {
    var key = (value || '').trim().toUpperCase();
    return Object.prototype.hasOwnProperty.call(SIZE_DISPLAY_NAMES, key) ? SIZE_DISPLAY_NAMES[key] : value;
  }

  function fetchCanonicalProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);

    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.json();
      })
      .catch(function () {
        return null;
      });

    cache.set(handle, promise);
    return promise;
  }

  function canonicalOption(data, position) {
    if (!data || !data.options) return null;
    // product_option.position is 1-based and matches the option's own index in this array.
    return data.options[position - 1] || null;
  }

  function correctTitleElement(el, data) {
    if (!data || !data.title) return;
    el.textContent = data.title;
  }

  function correctOptionNameElement(el, data) {
    var option = canonicalOption(data, Number(el.getAttribute('data-option-position')));
    if (!option) return;
    var name = option.name;
    if (el.getAttribute('data-downcase') === 'true') name = name.toLowerCase();
    el.textContent = name;
  }

  function canonicalValueText(el, data) {
    var option = canonicalOption(data, Number(el.getAttribute('data-option-position')));
    if (!option) return null;
    var value = option.values[Number(el.getAttribute('data-value-index'))];
    if (value === undefined) return null;
    if (el.getAttribute('data-size-value') === 'true') value = sizeDisplayName(value);
    return value;
  }

  function correctOptionValueElement(el, data) {
    var value = canonicalValueText(el, data);
    if (value === null) return;

    if (el.tagName === 'OPTION') {
      var text = value;
      if (el.getAttribute('data-combined-label') === 'true') {
        var option = canonicalOption(data, Number(el.getAttribute('data-option-position')));
        var name = option ? option.name : '';
        text = name.toLowerCase() + ': ' + value.toLowerCase();
      }
      var suffix = el.getAttribute('data-unavailable-suffix');
      if (suffix) text += suffix;
      el.textContent = text;
      return;
    }

    var downcase = el.getAttribute('data-downcase') === 'true';
    el.textContent = downcase ? value.toLowerCase() : value;
  }

  function correctAll(root) {
    var scope = root || document;
    var elements = scope.querySelectorAll(ALL_SELECTOR);
    var handles = new Set();
    elements.forEach(function (el) {
      handles.add(el.getAttribute('data-product-handle'));
    });

    handles.forEach(function (handle) {
      if (!handle) return;
      fetchCanonicalProduct(handle).then(function (data) {
        if (!data) return;
        scope.querySelectorAll(ALL_SELECTOR).forEach(function (el) {
          if (el.getAttribute('data-product-handle') !== handle) return;
          if (el.matches(TITLE_SELECTOR)) correctTitleElement(el, data);
          else if (el.matches(OPTION_NAME_SELECTOR)) correctOptionNameElement(el, data);
          else if (el.matches(OPTION_VALUE_SELECTOR)) correctOptionValueElement(el, data);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      correctAll();
    });
  } else {
    correctAll();
  }

  // Owner correction (2026-08-31, later same day): shopify:cart:lines-update turned out not to be
  // a usable signal here - confirmed live (performance.now() timestamps on both the event and the
  // marker actually appearing) that the cart drawer's own new line-item markup lands via its own
  // fetch-then-render pipeline roughly 900ms AFTER the event fires, not on the next frame - a
  // single requestAnimationFrame buffer (tried first) wasn't nearly enough, and there's no later
  // "drawer render complete" event exposed to wait for instead. A MutationObserver is the correct,
  // idiomatic tool for exactly this - reacting to content that appears asynchronously with no
  // reliable completion signal - not a workaround standing in for a real fix the way it would be
  // for something with an actual event to hook (contrast assets/currency-picker.js, which reacts
  // to the menu drawer's own native `toggle` event instead, because one exists there). Scoped
  // tightly: only inspects each mutation's own addedNodes for a marker class specifically (never
  // a blanket "re-scan on every DOM change"), so this stays idle through everything else that
  // touches the page.
  var cartObserver = new MutationObserver(function (mutations) {
    var found = false;
    for (var i = 0; i < mutations.length; i++) {
      var addedNodes = mutations[i].addedNodes;
      for (var j = 0; j < addedNodes.length; j++) {
        var node = addedNodes[j];
        if (!(node instanceof Element)) continue;
        if (node.matches(ALL_SELECTOR) || node.querySelector(ALL_SELECTOR)) {
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (found) correctAll();
  });

  function startCartObserver() {
    cartObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startCartObserver);
  } else {
    startCartObserver();
  }
})();
